import clsx from 'clsx'
import { Text } from '../ui'
import { useEffect, useMemo, useState } from 'react'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import isologo_SII from '../../../assets/isologo_SII.png'
import { formatCurrency, formatDate, formatRut } from '../../utils/formatter'
import translations from '../ui/traslation/traslationAdministracion.json'
import { getConciliationDisplayStatus } from '../../utils/conciliationStatus'
import { getBankAssignedAmount } from '../../utils/vinculacionAmounts'

const TablaCompensacion = ({
  data,
  idPrincipal,
  modalStatus,
  setReloadData,
  selectedIds,
  setSelectedIds,
  setInitialSelectedIds,
  unselectedIds,
  setUnselectedIds,
  dataLinkage,
  dataModal,
  onSort,
  order,
  onCurrentData,
  onCheckboxChange,
  setSelectedCheckboxes,
  selectedDataByOrigen,
  setSelectedDataByOrigen,
  montoBase
}) => {
  const [isInitialSelectionDone, setIsInitialSelectionDone] = useState(false)

  const getIdPrincipalFromItem = item => {
    if (!item) return undefined
    return (
      item?.idPrincipal ??
      item?.id_principal ??
      item?.egreso?.idPrincipal ??
      item?.egreso?.id_principal ??
      item?.ingreso?.idPrincipal ??
      item?.ingreso?.id_principal ??
      item?.linkage?.idPrincipal ??
      item?.linkage?.id_principal ??
      item?.id
    )
  }

  const safeSelectedIds = Array.isArray(selectedIds) ? selectedIds : []

  const extractIdsFromDataLinkage = dl => {
    if (!dl) return []
    if (Array.isArray(dl)) {
      return dl.map(d => d?.idPrincipal ?? d?.id_principal).filter(Boolean)
    }
    const ids = []
    if (dl?.idPrincipal) ids.push(dl.idPrincipal)
    if (dl?.id_principal) ids.push(dl.id_principal)
    // revisar si dataLinkage trae egreso/ingreso/linkage anidados
    ;['egreso', 'ingreso', 'linkage'].forEach(k => {
      const v = dl[k]
      if (!v) return
      if (Array.isArray(v)) {
        v.forEach(x => {
          if (x?.idPrincipal) ids.push(x.idPrincipal)
          if (x?.id_principal) ids.push(x.id_principal)
        })
      } else {
        if (v?.idPrincipal) ids.push(v.idPrincipal)
        if (v?.id_principal) ids.push(v.id_principal)
      }
    })
    return [...new Set(ids)]
  }

  const rawRows = data[data.seleccionado] || []

  const rows = rawRows.filter(item => {
    const status = item.status || ''
    return !status.includes('ANULA')
  })

  const rowsWithLinkage = useMemo(() => {
    const linkageRows = Array.isArray(dataLinkage) ? dataLinkage : []
    const map = new Map()

    rows.forEach(item => {
      const key = getIdPrincipalFromItem(item)
      map.set(key || JSON.stringify(item), item)
    })

    linkageRows.forEach(item => {
      if ((item?.nombreCuentaBanco || '').trim().length > 0) return
      const key = getIdPrincipalFromItem(item)
      if (!map.has(key || JSON.stringify(item))) {
        map.set(key || JSON.stringify(item), item)
      }
    })

    const combined = Array.from(map.values())

    combined.sort((a, b) => {
      const aKey = getIdPrincipalFromItem(a)
      const bKey = getIdPrincipalFromItem(b)
      const aSelected = aKey && safeSelectedIds.includes(aKey)
      const bSelected = bKey && safeSelectedIds.includes(bKey)
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      const aDate = new Date(a.fecha).getTime()
      const bDate = new Date(b.fecha).getTime()
      return aDate - bDate
    })

    return combined
  }, [rows, dataLinkage, selectedIds])

  const handleCheckboxToggle = id => {
    const isChecked = safeSelectedIds.includes(id)

    // 1) Actualiza selectedIds
    const updatedSelection = isChecked
      ? selectedIds.filter(sel => sel !== id)
      : [...selectedIds, id]

    setSelectedIds(updatedSelection) // viene del padre
    setSelectedCheckboxes?.(prev => ({ ...prev, [String(id)]: !isChecked }))

    // 2) Actualiza unselectedIds
    setUnselectedIds((prev = []) => (isChecked ? [...prev, id] : prev.filter(unId => unId !== id)))

    // 3) Notifica al padre los datos actuales para onCurrentData
    onCurrentData?.(rows.filter(row => updatedSelection.includes(getIdPrincipalFromItem(row))))
  }

  const styles = {
    wrapper: 'overflow-x-auto rounded-3xl shadow-sm',
    table: 'w-full table-auto text-[0.725rem] border-collapse leading-tight',
    thead: 'sticky top-0 z-10 bg-gray-50',
    th: 'py-2 px-1 w-auto max-w-[120px] text-center break-words border-b border-gray-200 uppercase text-[0.6rem] font-bold text-gray-500',
    tr: 'border-b border-gray-100 hover:bg-gray-50 transition-colors',
    td: 'py-2 px-2 w-auto max-w-[200px] text-center break-words align-middle',
    smallCol: 'w-auto max-w-[80px]',
    wideCol: 'w-auto max-w-[300px]',
    boldText: 'font-bold text-[0.65rem]',
    text: 'text-[0.705rem] text-gray-400 font-medium',
  }

  const baseCols = [
    { col: '', field: null },
    { col: 'Fecha', field: 'fecha' },
    { col: 'Razon Social', field: 'razonSocial' },
    { col: 'Cuenta Corriente', field: 'cuentaCorriente' },
    { col: 'Descripción', field: 'descripción' },
    { col: 'Abono', field: 'montoAbono', type: 'abono' },
    { col: 'Cargo', field: 'montoCargo', type: 'cargo' },
    { col: 'Monto asignado', field: null },
    { col: 'Status', field: 'status' },
    { col: 'Acciones', field: 'acciones' }
  ]

  const showCargoStatuses = ['POR COBRAR', 'PARCIALMENTE COBRADO', 'COBRADO']

  const orderableCols = baseCols.filter(col => {
    if (col.type === 'abono' && showCargoStatuses.includes(modalStatus)) return false
    if (col.type === 'cargo' && !showCargoStatuses.includes(modalStatus)) return false
    return true
  })

  const getSortValue = (row, field) => {
    switch (field) {
      case 'fecha':
        return row.fecha
      case 'razonSocial':
        return row.razonSocial
      case 'cuentaCorriente':
        return row.nombreCuenta ?? row.cuentaCorriente
      case 'descripción':
        return row.descripcion ?? row.nombreFolio ?? row.glosa
      case 'montoAbono':
        return row.montoAbono ?? row.montoTotal ?? 0
      case 'montoCargo':
        return row.montoCargo ?? row.montoTotal ?? 0
      case 'status':
        return row.status
      default:
        return row[field]
    }
  }

  const getMontoCompensacion = row =>
    Number(
      row?.montoAbono ??
      row?.montoCargo ??
      row?.montoRelacionado ??
      row?.debe ??
      row?.haber ??
      0
    )

  const linkageIdSet = useMemo(
    () => new Set(extractIdsFromDataLinkage(dataLinkage).map(id => String(id))),
    [dataLinkage]
  )

  const compareLinkagePriority = (a, b) => {
    const aLinked = linkageIdSet.has(String(getIdPrincipalFromItem(a)))
    const bLinked = linkageIdSet.has(String(getIdPrincipalFromItem(b)))
    if (aLinked === bLinked) return 0
    return aLinked ? -1 : 1
  }

  const sortedRows = useMemo(() => {
    if (!rowsWithLinkage?.length) return []

    if (!order?.field && montoBase != null) {
      return [...rowsWithLinkage].sort((a, b) => {
        const linkagePriority = compareLinkagePriority(a, b)
        if (linkagePriority !== 0) return linkagePriority
        const diffA = Math.abs(getMontoCompensacion(a) - montoBase)
        const diffB = Math.abs(getMontoCompensacion(b) - montoBase)
        return diffA - diffB
      })
    }

    const direction = order.direction === 'desc' ? -1 : 1
    return [...rowsWithLinkage].sort((a, b) => {
      const linkagePriority = compareLinkagePriority(a, b)
      if (linkagePriority !== 0) return linkagePriority
      const aValue = getSortValue(a, order.field)
      const bValue = getSortValue(b, order.field)

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      if (order.field === 'fecha') {
        return (new Date(aValue) - new Date(bValue)) * direction
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * direction
      }

      return aValue
        .toString()
        .localeCompare(bValue.toString(), 'es-CL') * direction
    })
  }, [rowsWithLinkage, order, montoBase, linkageIdSet])

  const obtenerAbreviatura = nombre => {
    return translations.documentosContables[nombre] || nombre
  }

  useEffect(() => {
    if (!rowsWithLinkage || !rowsWithLinkage.length) return

    if (!Array.isArray(selectedIds)) {
      setSelectedIds([])
    } 

    const linkageIds = extractIdsFromDataLinkage(dataLinkage)
    const newSelection = rowsWithLinkage
      .map(item => getIdPrincipalFromItem(item))
      .filter(Boolean)
      .filter(itemId => {
        if (idPrincipal && itemId === idPrincipal) return true
        if (linkageIds.length && linkageIds.includes(itemId)) return true
        return false
      })

    const uniqueSelection = [...new Set(newSelection)]
    if (uniqueSelection.length && !isInitialSelectionDone) {
      setSelectedIds(uniqueSelection)
      setInitialSelectedIds(uniqueSelection)
      setIsInitialSelectionDone(true)

      const selectedData = rowsWithLinkage
        .filter(row => uniqueSelection.includes(getIdPrincipalFromItem(row)))
        .map(row => ({ ...row, _origen: 'tc' }))
      onCheckboxChange?.(uniqueSelection)
      onCurrentData?.(selectedData)
      setSelectedCheckboxes?.(prev => {
        const next = { ...prev }
        uniqueSelection.forEach(id => {
          next[String(id)] = true
        })
        return next
      })
      setSelectedDataByOrigen(prev => ({
        ...prev,
        tc: selectedData
      }))
    }
  }, [rowsWithLinkage, idPrincipal, JSON.stringify(dataLinkage)])

  useEffect(() => {
    setIsInitialSelectionDone(false)
  }, [JSON.stringify(dataLinkage), dataModal?.id])
  
  const getCleanStatus = row =>
      typeof row?.status === 'string'
        ? row.status.replace(/\s*undefined\s*/gi, '').trim()
        : ''
  
  const normalizeStatus = status =>
      String(status || '')
        .replace(/\s*undefined\s*/gi, '')
        .trim()
        .toUpperCase()
  
  const statusTone = status => {
    const s = normalizeStatus(status)
    if (s.includes('PARCIAL')) return 'warning'
    if (s.includes('PAGADO') || s.includes('COBRADO')) return 'success'
    if (s.includes('POR PAGAR') || s.includes('POR COBRAR')) return 'danger'
    return 'muted'
  }
  
    const statusBadgeClass = status => {
      const tone = statusTone(status)
      return clsx(
        'inline-flex items-center justify-center px-3 py-1 rounded-full text-[0.6rem] sm:text-[0.65rem] font-semibold uppercase tracking-wider',
        {
          'text-danger bg-danger/10': tone === 'danger',
          'text-success bg-success/10': tone === 'success',
          'text-amber-700 bg-amber-100': tone === 'warning',
          'text-muted bg-border/40': tone === 'muted'
        }
      )
    }

    const truncateText = (text, max = 15) => {
      if (!text) return ''
      return text.length > max ? text.slice(0, max) + '...' : text
    }

  return (
    <div className={styles.wrapper}>
      <div className=' mt-2'>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              {orderableCols.map(({ col, field }, i) => (
                <th
                  className={clsx(
                    styles.th,
                    { 'cursor-pointer': field },
                    i === 0 && styles.mediumCol
                  )}
                  onClick={() => field && onSort({ col, field })}
                  key={i}
                >
                  {col}
                  {order.col === col && (
                    <KeyboardArrowDownIcon
                      className={clsx('transition-all ml-2 cursor-pointer', {
                        'opacity-40': order.col !== col,
                        'rotate-180': order.direction === 'desc'
                      })}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {sortedRows.length > 0 ? (
              sortedRows.map((item, index) => {
                const rowId = getIdPrincipalFromItem(item)
                const isChecked = Array.isArray(selectedIds)
                  ? selectedIds.includes(getIdPrincipalFromItem(item))
                  : false
                const cleanStatus = getCleanStatus(item)
                const assignedAmount = getBankAssignedAmount(
                  { ...item, idPrincipal: rowId },
                  (Array.isArray(dataLinkage) ? dataLinkage : dataLinkage ? [dataLinkage] : []).map(link => ({
                    ...link, idPrincipal: getIdPrincipalFromItem(link)
                  })),
                  dataModal,
                  isChecked
                )
                const displayStatus = getConciliationDisplayStatus(cleanStatus, modalStatus, isChecked)
                return (
                  <tr className={styles.tr} key={index}>
                    <td className={clsx(styles.td, styles.smallCol, 'text-center')}>
                      <div className='flex items-center justify-center gap-1'>
                        <input
                          type='checkbox'
                          className='rounded'
                          checked={isChecked}
                          onChange={() => handleCheckboxToggle(getIdPrincipalFromItem(item))}
                        />

                        <img src={isologo_SII} className='h-6 w-auto rounded' />
                      </div>
                    </td>
                    <td className={clsx(styles.td, styles.smallCol)}>
                      <Text className={styles.boldText}>{formatDate(item.fecha)}</Text>
                    </td>
                    <td className={clsx(styles.td, styles.wideCol)}>
                      <Text className={clsx(styles.boldText, 'truncate')}  title={item.razonSocial}>{truncateText(item.razonSocial)}</Text>
                    </td>
                    <td className={clsx(styles.td, styles.wideCol)}>
                      <Text className={clsx(styles.boldText, 'truncate')}  title={item.nombreCuenta}>{truncateText(item.nombreCuenta)}</Text>
                      <Text className={styles.text}>{formatRut(item.rut)}</Text>
                    </td>

                    <td className={clsx(styles.td, styles.wideCol)}>
                      <Text className={styles.boldText}>
                        {obtenerAbreviatura(item.nombreFolio)}
                      </Text>
                      <Text className={styles.text}>{item.folio}</Text>
                    </td>

                    {/* Aquí solo uno de los dos */}
                    {showCargoStatuses.includes(modalStatus) ? (
                      <td className={clsx(styles.td, styles.mediumCol)}>
                        <Text className={styles.boldText}>{formatCurrency(item.montoTotal)}</Text>
                      </td>
                    ) : (
                      <td className={clsx(styles.td, styles.mediumCol)}>
                        <Text className={styles.boldText}>{formatCurrency(item.montoTotal)}</Text>
                      </td>
                    )}
                    <td className={clsx(styles.td, styles.mediumCol)}>
                      <Text className={styles.boldText}>{assignedAmount == null ? '—' : formatCurrency(assignedAmount)}</Text>
                    </td>
                    <td className={clsx(styles.td, styles.mediumCol)}>
                      <span className={statusBadgeClass(displayStatus)}>
                        {displayStatus || '—'}
                      </span>
                    </td>
                    <td className={clsx(styles.td, styles.mediumCol)}>
                      <div className='flex h-full items-center justify-center space-x-3'>
                        <div className='flex flex-col text-xs text-gray-500 items-center justify-center hover:cursor-pointer'>
                          <button
                            className={clsx(
                              'flex items-center justify-center w-7 h-7 rounded-full shadow-full-xs transition-colors duration-150',
                              {
                                'bg-accent2 text-white hover:bg-accent2/90 cursor-pointer':
                                  isChecked,
                                'bg-gray-100 text-gray-300 hover:bg-gray-200 cursor-pointer':
                                  !isChecked
                              }
                            )}
                            onClick={() => handleCheckboxToggle(rowId)}
                          >
                            <DoneAllIcon className='w-4 h-4' />
                          </button>
                          <Text className='mt-2'>{isChecked ? 'Seleccionado' : 'Seleccionar'}</Text>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={orderableCols.length} className='text-center py-4'>
                  No hay datos disponibles
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TablaCompensacion
